import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  applyFrameZoneToLoadCase,
  buildBasisRows,
  buildBottomSegmentRows,
  buildFrameZoneRows,
  buildLoadingsSvgString,
  buildRoofSegmentRows,
  buildRoofZoneSegmentRows,
  buildSnowSegmentRows,
  buildWindCaseRows,
  buildWindZonesTableRows,
  resolveLoadingCases,
} from './trussLoadingsShared';

export default function TrussLoadingsView({ results, inputs }) {
  const geom = results?.geometry;
  const { cases, windMeta, snowMeta } = useMemo(
    () => resolveLoadingCases(results, inputs),
    [results, inputs],
  );
  const [caseId, setCaseId] = useState(cases[0]?.id || '');
  const [frameIdx, setFrameIdx] = useState(0);

  const activeId = cases.some((c) => c.id === caseId) ? caseId : cases[0]?.id || '';
  const baseCase = cases.find((c) => c.id === activeId);

  const frameZones = baseCase?.frameZones || baseCase?.detail?.frame_zones || [];
  const hasFrames = Array.isArray(frameZones) && frameZones.length > 1;

  useEffect(() => {
    setFrameIdx(0);
  }, [activeId]);

  const loadCase = useMemo(() => {
    if (!baseCase) return null;
    if (!hasFrames) return applyFrameZoneToLoadCase(baseCase, null);
    return applyFrameZoneToLoadCase(baseCase, frameIdx);
  }, [baseCase, frameIdx, hasFrames]);

  const basisRows = useMemo(
    () => buildBasisRows(results, inputs, windMeta, snowMeta),
    [results, inputs, windMeta, snowMeta],
  );

  const svgHtml = useMemo(() => {
    if (!loadCase || !geom) return '';
    return buildLoadingsSvgString(geom, loadCase);
  }, [geom, loadCase]);

  if (!cases.length) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No characteristic EN snow/wind load data on this result. Run analysis with Eurocode wind/snow models enabled.
      </Alert>
    );
  }

  const windRows = loadCase?.kind === 'wind' ? buildWindCaseRows(loadCase) : [];
  const zoneTableRows = loadCase?.kind === 'wind' ? buildWindZonesTableRows(loadCase, windMeta) : [];
  const roofZoneRows = loadCase?.kind === 'wind' ? buildRoofZoneSegmentRows(loadCase) : [];
  const frameZoneRows = baseCase?.kind === 'wind' ? buildFrameZoneRows(baseCase) : [];
  const snowRows =
    (loadCase?.id === 'snow' || loadCase?.id === 'snow_case_ii') &&
    Array.isArray(loadCase?.detail?.mu_per_seg) &&
    loadCase.detail.mu_per_seg.length
      ? buildSnowSegmentRows(loadCase, geom)
      : [];
  const bottomRows = loadCase?.kind === 'bottom' ? buildBottomSegmentRows(loadCase, geom) : [];
  const roofRows =
    loadCase && !snowRows.length && loadCase.kind !== 'wind' && loadCase.kind !== 'bottom'
      ? buildRoofSegmentRows(loadCase, geom)
      : loadCase?.kind === 'wind' && !roofZoneRows.length
        ? buildRoofSegmentRows(loadCase, geom)
        : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Load case</InputLabel>
          <Select label="Load case" value={activeId} onChange={(e) => setCaseId(e.target.value)}>
            {cases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.shortLabel || c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasFrames && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Frame</InputLabel>
            <Select
              label="Frame"
              value={frameIdx}
              onChange={(e) => setFrameIdx(Number(e.target.value))}
            >
              {frameZones.map((fz, i) => (
                <MenuItem key={fz.frame ?? i} value={i}>
                  Frame {fz.frame ?? i}
                  {fz.tributary_m != null ? ` (trib ${Number(fz.tributary_m).toFixed(1)} m)` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {loadCase?.kind === 'wind' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {windMeta?.axis_convention ||
            'Building X = length (frames); Building Y = span; Z = up.'}
          {loadCase.frameDebug ? ` · ${loadCase.frameDebug}` : ''}
        </Typography>
      )}

      {!hasFrames && inputs?.building_mode && loadCase?.kind === 'wind' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Per-frame wind zones are not on this result yet — re-run analysis to populate frame loads.
        </Alert>
      )}

      {svgHtml ? (
        <Paper variant="outlined" sx={{ p: 1, mb: 2, overflow: 'auto' }}>
          <Box dangerouslySetInnerHTML={{ __html: svgHtml }} />
        </Paper>
      ) : null}

      {basisRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Basis
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableBody>
                {basisRows.map((r) => (
                  <TableRow key={r.label}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell>{r.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {zoneTableRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Relevant wind zones (EN 1991-1-4)
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zone</TableCell>
                  <TableCell>Surface</TableCell>
                  <TableCell>Extent</TableCell>
                  <TableCell align="right">c_pe</TableCell>
                  <TableCell align="right">Length (m)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zoneTableRows.map((r) => (
                  <TableRow key={`${r.zone}-${r.surface}`}>
                    <TableCell>{r.zone}</TableCell>
                    <TableCell>{r.surface}</TableCell>
                    <TableCell>{r.extent}</TableCell>
                    <TableCell align="right">{r.cpe}</TableCell>
                    <TableCell align="right">{r.length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {windRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Wind case{loadCase?.frameIndex != null ? ` · Frame ${loadCase.frameIndex}` : ''}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableBody>
                {windRows.map((r) => (
                  <TableRow key={r.label}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell>{r.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {roofZoneRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Roof zones per segment
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Seg</TableCell>
                  <TableCell>x₀ (m)</TableCell>
                  <TableCell>x₁ (m)</TableCell>
                  <TableCell>Zones</TableCell>
                  <TableCell align="right">c_pe,ext</TableCell>
                  <TableCell align="right">c_pe,net</TableCell>
                  <TableCell align="right">w (kN/m)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roofZoneRows.map((r) => (
                  <TableRow key={r.seg}>
                    <TableCell>{r.seg}</TableCell>
                    <TableCell>{r.x0}</TableCell>
                    <TableCell>{r.x1}</TableCell>
                    <TableCell>{r.zones}</TableCell>
                    <TableCell align="right">{r.cpeExt}</TableCell>
                    <TableCell align="right">{r.cpeNet}</TableCell>
                    <TableCell align="right">{r.w}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {frameZoneRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Per-frame zones (debug)
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Frame</TableCell>
                  <TableCell>y₀ (m)</TableCell>
                  <TableCell>y₁ (m)</TableCell>
                  <TableCell>Trib (m)</TableCell>
                  <TableCell>Wall zones</TableCell>
                  <TableCell>Roof zones</TableCell>
                  <TableCell align="right">q_L</TableCell>
                  <TableCell align="right">q_R</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {frameZoneRows.map((r) => (
                  <TableRow
                    key={r.frame}
                    selected={hasFrames && Number(r.frame) === Number(frameZones[frameIdx]?.frame ?? frameIdx)}
                  >
                    <TableCell>{r.frame}</TableCell>
                    <TableCell>{r.y0}</TableCell>
                    <TableCell>{r.y1}</TableCell>
                    <TableCell>{r.trib}</TableCell>
                    <TableCell>{r.wallZones}</TableCell>
                    <TableCell>{r.roofZones}</TableCell>
                    <TableCell align="right">{r.qLeft}</TableCell>
                    <TableCell align="right">{r.qRight}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {snowRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Snow per segment
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Seg</TableCell>
                  <TableCell>x₀ (m)</TableCell>
                  <TableCell>x₁ (m)</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">μ</TableCell>
                  <TableCell align="right">s (kN/m²)</TableCell>
                  <TableCell align="right">w (kN/m)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snowRows.map((r) => (
                  <TableRow key={r.seg}>
                    <TableCell>{r.seg}</TableCell>
                    <TableCell>{r.x0}</TableCell>
                    <TableCell>{r.x1}</TableCell>
                    <TableCell>{r.side}</TableCell>
                    <TableCell align="right">{r.mu}</TableCell>
                    <TableCell align="right">{r.s}</TableCell>
                    <TableCell align="right">{r.w}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {bottomRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Bottom chord line load per segment
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Seg</TableCell>
                  <TableCell>x₀ (m)</TableCell>
                  <TableCell>x₁ (m)</TableCell>
                  <TableCell align="right">w (kN/m)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bottomRows.map((r) => (
                  <TableRow key={r.seg}>
                    <TableCell>{r.seg}</TableCell>
                    <TableCell>{r.x0}</TableCell>
                    <TableCell>{r.x1}</TableCell>
                    <TableCell align="right">{r.w}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {roofRows.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {loadCase?.id === 'snow' ? 'Snow line load per segment' : 'Roof line load per segment'}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Seg</TableCell>
                  <TableCell>x₀ (m)</TableCell>
                  <TableCell>x₁ (m)</TableCell>
                  <TableCell>Zones</TableCell>
                  <TableCell align="right">w (kN/m)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roofRows.map((r) => (
                  <TableRow key={r.seg}>
                    <TableCell>{r.seg}</TableCell>
                    <TableCell>{r.x0}</TableCell>
                    <TableCell>{r.x1}</TableCell>
                    <TableCell>{r.zones}</TableCell>
                    <TableCell align="right">{r.w}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
